import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../../../lib/toast-utils';
import { useAuth } from '../../../lib/auth/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Badge } from '../../ui/badge';
import { Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { Id } from '../../../convex/_generated/dataModel';

export const ProjectList: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, checkAndRefreshAuth, useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Use authenticated API calls that automatically include session tokens
  const projectsQuery = useAuthenticatedQuery('projects:list', {});
  
  // Safely handle the projects array
  const projects = Array.isArray(projectsQuery) ? projectsQuery : [];
  
  const updateProject = useAuthenticatedMutation('projects:update');
  const deleteProject = useAuthenticatedMutation('projects:deleteProject');
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  // Try to refresh auth when component mounts or authentication state changes
  useEffect(() => {
    const checkAuth = async () => {
      console.log("Checking authentication state:", { isAuthenticated, authLoading, retryCount });
      
      if (!isAuthenticated && !authLoading) {
        console.log("Attempting to refresh authentication...");
        setIsLoadingProjects(true);
        try {
          const refreshed = await checkAndRefreshAuth();
          console.log("Auth refresh result:", refreshed);
          
          // If refreshed successfully, mark auth as checked
          if (refreshed) {
            setAuthChecked(true);
          } else if (retryCount < 2) {
            // Retry a couple of times if needed
            setRetryCount(prev => prev + 1);
            // Wait a bit before retrying
            setTimeout(() => setAuthChecked(false), 500);
            return;
          }
        } catch (error) {
          console.error("Auth refresh failed:", error);
        }
      }
      
      // Mark auth as checked regardless of result after retries
      setAuthChecked(true);
      setIsLoadingProjects(false);
    };
    
    checkAuth();
  }, [isAuthenticated, authLoading, checkAndRefreshAuth, retryCount]);

  // Handle query errors gracefully
  useEffect(() => {
    if (projectsQuery === undefined && isAuthenticated && authChecked && !authLoading) {
      // If the query is still loading but we're authenticated, wait
      console.log("Projects query is loading...");
    } else if (projectsQuery === null && isAuthenticated && authChecked) {
      console.error("Projects query failed");
      toast({
        title: 'Error',
        description: 'Failed to load projects. Please try again.',
        variant: 'destructive',
      });
    }
  }, [projectsQuery, isAuthenticated, authChecked, authLoading]);

  const handleStatusChange = async (projectId: string, status: string) => {
    setIsLoadingProjects(true);
    try {
      await updateProject({
        projectId,
        status,
      });
      toast({
        title: 'Status updated',
        description: `Project is now ${status}`,
      });
    } catch (error) {
      console.error('Error updating project status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update project status',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleDeleteClick = (projectId: string) => {
    setProjectToDelete(projectId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    setIsLoadingProjects(true);
    try {
      await deleteProject({
        projectId: projectToDelete,
      });
      
      toast({
        title: 'Project deleted',
        description: 'Project has been permanently deleted',
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      
      toast({
        title: 'Error',
        description: 'Failed to delete project',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingProjects(false);
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'published':
        return <Badge variant="success">Published</Badge>;
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (authLoading || isLoadingProjects) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-gray-500">
            {authLoading ? "Verifying authentication..." : "Loading projects..."}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md text-center">
        <h3 className="text-lg font-medium text-yellow-800 mb-2">Authentication Required</h3>
        <p className="text-yellow-600 mb-4">
          You need to be logged in to view projects. If you were recently logged in, 
          your session may have expired.
        </p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
          <Button variant="outline" onClick={async () => {
            setIsLoadingProjects(true);
            const success = await checkAndRefreshAuth();
            setAuthChecked(true);
            setIsLoadingProjects(false);
            if (!success) {
              toast({
                title: 'Authentication Failed',
                description: 'Unable to restore your session. Please log in again.',
                variant: 'destructive',
              });
            }
          }}>
            Retry Authentication
          </Button>
        </div>
      </div>
    );
  }

  // If we're authenticated but projects are undefined (still loading)
  if (projectsQuery === undefined) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-gray-500">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Projects</h2>
        <Button onClick={() => navigate('/admin/projects/new')}>
          Create New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="bg-gray-50 p-12 text-center rounded-md">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first project</p>
          <Button onClick={() => navigate('/admin/projects/new')}>
            Create Project
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project._id}>
                <TableCell className="font-medium">{project.name}</TableCell>
                <TableCell>{project.slug}</TableCell>
                <TableCell>{getStatusBadge(project.status)}</TableCell>
                <TableCell>{formatDate(project.createdAt)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Manage Project</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => navigate(`/admin/projects/${project._id}/edit`)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/admin/projects/${project._id}/builder`)}>
                        Page Builder
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Status</DropdownMenuLabel>
                      <DropdownMenuItem 
                        disabled={project.status === 'draft' || isLoadingProjects}
                        onClick={() => handleStatusChange(project._id, 'draft')}
                      >
                        Set as Draft
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        disabled={project.status === 'published' || isLoadingProjects}
                        onClick={() => handleStatusChange(project._id, 'published')}
                      >
                        Publish
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        disabled={project.status === 'archived' || isLoadingProjects}
                        onClick={() => handleStatusChange(project._id, 'archived')}
                      >
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteClick(project._id)}
                        disabled={isLoadingProjects}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the project
              and all its page components.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoadingProjects}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isLoadingProjects}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoadingProjects ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}; 